import React, { useState, useMemo } from 'react'
import type { FixtureBuilderOut, BuilderAngle, MarketCategory, CategoryAngles } from '../types'
import { AngleCard } from './AngleCard'
import { PlayerMarketCard } from './PlayerMarketCard'
import { MatchOddsMarketCard } from './MatchOddsMarketCard'
import { FixtureContextBanner } from './FixtureContextBanner'

// New category-based filter structure (matching builder_lines_structure.md order)
type CategoryConfig = {
  key: MarketCategory | 'all'
  label: string
}

const CATEGORY_CONFIGS: CategoryConfig[] = [
  { key: 'all', label: 'All' },
  { key: 'match_result', label: 'Match Result' },
  { key: 'fouls_cards', label: 'Fouls & Cards' },
  { key: 'goalscorer', label: 'Goalscorer' },
  { key: 'goals', label: 'Goals' },
  { key: 'corners', label: 'Corners' },
  { key: 'shots', label: 'Shots' },
  { key: 'other', label: 'Other Markets' },
]

// Market type labels for display (matching builder_lines_structure.md names exactly)
const MARKET_LABELS: Record<string, string> = {
  // Match Result (order from builder_lines_structure.md)
  'match-winner': 'Match odds',
  'draw-no-bet': 'Draw no bet',
  'double-chance': 'Double Chance',
  'match-odds-ou-goals': 'Match odds & over/under goals',

  // Fouls & Cards (order from builder_lines_structure.md)
  'player-booked': 'Player shown a card',
  'player-fouls-over-under': 'Player to commit a foul',
  'player-fouled-over-under': 'Player to be fouled',
  'team-fouls-over-under': 'Team fouls',
  'match-fouls-over-under': 'Match fouls',
  'match-cards-over-under': 'Cards Over/Under markets',
  'team-cards-over-under': 'Team total cards',
  'home-cards-over-under': 'Home team total cards',
  'away-cards-over-under': 'Away team total cards',
  'team-most-cards': 'Team to receive the most cards',
  'team-most-fouls-half': 'Team to commit most fouls in each half',
  'most-cards-half': 'Most cards in each half',
  'home-cards-half': 'Home cards in each half',
  'away-cards-half': 'Away cards in each half',
  'yellow-cards-over-under': 'Yellow Cards Over/Under',
  'home-yellow-cards-over-under': 'Home Team Yellow Cards',
  'away-yellow-cards-over-under': 'Away Team Yellow Cards',

  // Goalscorer (order from builder_lines_structure.md)
  'anytime-goalscorer': 'Player to score',
  'player-multi-goals': 'Player to score multiple goals',
  'score-or-assist': 'To score or assist',
  'score-or-card': 'To score or be shown a card',
  'anytime-assist': 'Anytime assist',

  // Goals (order from builder_lines_structure.md)
  'match-goals-over-under': 'Over/Under Goals market',
  'goals-range-match': 'Goals Range - Match',
  'goals-range-home': 'Goals Range - Home',
  'goals-range-away': 'Goals Range - Away',
  'team-goals-over-under': 'Team Over/Under Goals',
  'home-goals-over-under': 'Home Team Over/Under Goals',
  'away-goals-over-under': 'Away Team Over/Under Goals',
  'team-goals-conceded': 'Goals Conceded',
  'result-btts': 'Result & Both to score',
  'btts': 'Both Teams to score',
  'btts-ou-25': 'Both teams to score & O/U 2.5 Goals',

  // Corners (order from builder_lines_structure.md)
  'match-corners-over-under': 'Corners Over/Under',
  'team-corners-over-under': 'Team Total corners',
  'home-corners-over-under': 'Home Total corners',
  'away-corners-over-under': 'Away total corners',
  'corners-match-bet': 'Corner match bet',

  // Shots (order from builder_lines_structure.md)
  'player-sot-over-under': 'Player shots on target',
  'team-sot-over-under': 'Team shots on target',
  'player-shots-over-under': 'Player shots',
  'team-shots-over-under': 'Team shots',
  'match-shots-over-under': 'Match shots',
  'match-sot-over-under': 'Match shots on target',

  // Other Markets (order from builder_lines_structure.md)
  'goalkeeper-saves-over-under': 'Goalkeeper saves',
}

interface BuilderTabProps {
  builder: FixtureBuilderOut | null
  fixtureId: number
  fixtureLabel: string
  homeTeamId?: number
  awayTeamId?: number
}

export function BuilderTab({ builder, fixtureId, fixtureLabel, homeTeamId, awayTeamId }: BuilderTabProps) {
  const [activeCategory, setActiveCategory] = useState<MarketCategory | 'all'>('all')
  const [expandedMarkets, setExpandedMarkets] = useState<Set<string>>(new Set())

  // Check if we have the new categories structure
  const hasCategories = builder?.categories && Object.keys(builder.categories).length > 0

  // Get all categories to display
  const categoriesToShow = useMemo(() => {
    if (!hasCategories || !builder?.categories) return []

    if (activeCategory === 'all') {
      // Show all categories that have markets
      return Object.entries(builder.categories)
        .filter(([_, cat]) => Object.keys(cat.markets).length > 0)
        .sort((a, b) => {
          // Sort by category order
          const orderA = CATEGORY_CONFIGS.findIndex((c) => c.key === a[0])
          const orderB = CATEGORY_CONFIGS.findIndex((c) => c.key === b[0])
          return orderA - orderB
        })
    }

    // Show only selected category
    const cat = builder.categories[activeCategory as MarketCategory]
    return cat ? [[activeCategory, cat] as [string, CategoryAngles]] : []
  }, [builder?.categories, activeCategory, hasCategories])

  // Toggle market accordion
  const toggleMarket = (marketType: string) => {
    setExpandedMarkets((prev) => {
      const next = new Set(prev)
      if (next.has(marketType)) {
        next.delete(marketType)
      } else {
        next.add(marketType)
      }
      return next
    })
  }

  // Get market label
  const getMarketLabel = (marketType: string) => {
    return MARKET_LABELS[marketType] || marketType.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  }

  // Render a market accordion
  const renderMarketAccordion = (marketType: string, angles: BuilderAngle[]) => {
    const isExpanded = expandedMarkets.has(marketType)
    const topAngles = angles.slice(0, 3)
    const layoutType = angles[0]?.layout_type || 'grid'

    return (
      <div key={marketType} className="market-accordion">
        <div
          className={`accordion-header ${isExpanded ? 'expanded' : ''}`}
          onClick={() => toggleMarket(marketType)}
        >
          <span className="market-title">{getMarketLabel(marketType)}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="market-count">
              {angles.length} {angles.length === 1 ? 'market' : 'markets'}
            </span>
            <span className="accordion-icon">{isExpanded ? '▼' : '▶'}</span>
          </div>
        </div>

        {isExpanded && (
          <div className="accordion-content">
            {layoutType === 'player_list' ? (
              <PlayerMarketCard
                marketGroup={getMarketLabel(marketType)}
                angles={angles}
                fixtureId={fixtureId}
                fixtureLabel={fixtureLabel}
                alwaysExpanded={true}
              />
            ) : layoutType === 'match_odds' ? (
              <MatchOddsMarketCard
                marketType={marketType}
                marketLabel={getMarketLabel(marketType)}
                angles={angles}
                fixtureId={fixtureId}
                fixtureLabel={fixtureLabel}
              />
            ) : (
              <div className="grid" style={{ gridTemplateColumns: '1fr', gap: 8 }}>
                {angles.map((a, idx) => (
                  <AngleCard
                    key={idx}
                    angle={a}
                    fixtureId={fixtureId}
                    fixtureLabel={fixtureLabel}
                    homeTeamId={homeTeamId}
                    awayTeamId={awayTeamId}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  // Legacy fallback: filter angles when categories not available
  const legacyFilterAngles = (angles: BuilderAngle[]): BuilderAngle[] => {
    if (activeCategory === 'all') return angles
    return angles.filter((a) => a.group === activeCategory)
  }

  const filteredTeamAngles = useMemo(
    () => legacyFilterAngles(builder?.angles_team ?? []),
    [builder?.angles_team, activeCategory]
  )

  const filteredPlayerAngles = useMemo(
    () => legacyFilterAngles(builder?.angles_player ?? []),
    [builder?.angles_player, activeCategory]
  )

  // Legacy: Group player angles by market type
  const playerAnglesByGroup = useMemo(() => {
    const groups: Record<string, BuilderAngle[]> = {}
    for (const angle of filteredPlayerAngles) {
      const group = angle.group
      if (!groups[group]) {
        groups[group] = []
      }
      groups[group].push(angle)
    }
    return Object.entries(groups).sort((a, b) => b[1].length - a[1].length)
  }, [filteredPlayerAngles])

  // Get available categories for filter pills (only show ones with content)
  const availableCategories = useMemo(() => {
    if (!hasCategories || !builder?.categories) {
      return CATEGORY_CONFIGS.slice(0, 1) // Just 'All' for legacy
    }
    const available = new Set<string>(['all'])
    for (const key of Object.keys(builder.categories)) {
      if (Object.keys(builder.categories[key as MarketCategory]?.markets || {}).length > 0) {
        available.add(key)
      }
    }
    return CATEGORY_CONFIGS.filter((c) => available.has(c.key))
  }, [builder?.categories, hasCategories])

  return (
    <div className="builder-tab">
      {/* Fixture context banner (referee, derby, etc.) */}
      <FixtureContextBanner referee={builder?.referee} classification={builder?.classification} />

      {/* Category filter pills */}
      <div className="builder-filter-bar">
        {availableCategories.map((category) => (
          <button
            key={category.key}
            className={`chip ${activeCategory === category.key ? 'chip-active' : ''}`}
            onClick={() => setActiveCategory(category.key)}
          >
            {category.label}
          </button>
        ))}
      </div>

      {/* New category-based layout */}
      {hasCategories ? (
        <div className="builder-markets">
          {categoriesToShow.map(([catKey, catData]) => (
            <div key={catKey} className="builder-category">
              {activeCategory === 'all' && (
                <h3 className="builder-category-title">
                  {(catData as CategoryAngles).label}
                </h3>
              )}
              {Object.entries((catData as CategoryAngles).markets).map(([marketType, angles]) =>
                renderMarketAccordion(marketType, angles as BuilderAngle[])
              )}
            </div>
          ))}
          {categoriesToShow.length === 0 && (
            <div className="builder-empty">No markets match this category.</div>
          )}
        </div>
      ) : (
        /* Legacy layout */
        <div className="builder-markets">
          <div className="builder-category">
            <h3 className="builder-category-title">Team Markets</h3>
            <div className="builder-angles-grid">
              {(activeCategory === 'all' ? filteredTeamAngles.slice(0, 10) : filteredTeamAngles).map((a, idx) => (
                <AngleCard
                  key={idx}
                  angle={a}
                  fixtureId={fixtureId}
                  fixtureLabel={fixtureLabel}
                  homeTeamId={homeTeamId}
                  awayTeamId={awayTeamId}
                />
              ))}
              {!builder && (
                <div className="builder-empty">
                  No builder markets yet. Ingest some completed fixtures to generate rollups.
                </div>
              )}
              {builder && filteredTeamAngles.length === 0 && (
                <div className="builder-empty">No team markets match this filter.</div>
              )}
            </div>
          </div>

          <div className="builder-category">
            <h3 className="builder-category-title">Player Markets</h3>
            <div className="builder-angles-grid">
              {playerAnglesByGroup.map(([group, angles]) => (
                <PlayerMarketCard
                  key={group}
                  marketGroup={group}
                  angles={angles}
                  fixtureId={fixtureId}
                  fixtureLabel={fixtureLabel}
                />
              ))}
              {builder && playerAnglesByGroup.length === 0 && (
                <div className="builder-empty">No player markets match this filter.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
